import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { FROM_REFER_PARAM } from '../hooks/useGmailInvite';
import { gmailInviteService, INVITE_TOKEN_KEY } from '../services/gmailInviteService';

import { InvitePeopleModal } from './InvitePeopleModal';
import { ReferralModal } from './ReferralModal';

const RETURN_PARAMS = ['gmail_invites', 'gmail_invites_error'];
const STRIPPED_PARAMS = [...RETURN_PARAMS, FROM_REFER_PARAM];

const takeToken = (): string | null => {
  try {
    const token = localStorage.getItem(INVITE_TOKEN_KEY);

    localStorage.removeItem(INVITE_TOKEN_KEY);

    return token;
  } catch {
    // Blocked storage: the email match still counts the signup.
    return null;
  }
};

/** Google lands back with a full page load, so the parameters are read once, when the app starts. */
const readReturn = () => {
  const params = new URLSearchParams(window.location.search);

  return {
    open: RETURN_PARAMS.some((name) => params.has(name)),
    notice: params.get('gmail_invites_error'),
    // Invite all was pressed in Refer & earn, so that is the window to reopen.
    refer: params.get(FROM_REFER_PARAM) === 'refer',
  };
};

const useStripReturnParams = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (!STRIPPED_PARAMS.some((name) => params.has(name))) return;
    STRIPPED_PARAMS.forEach((name) => params.delete(name));
    const search = params.toString() ? `?${params.toString()}` : '';

    navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true });
  }, [location.search, location.pathname, location.hash, navigate]);
};

/** Count the signup that followed an invite's link, once the new account is signed in. */
const useInviteClaim = (signedIn: boolean) => {
  useEffect(() => {
    const token = signedIn ? takeToken() : null;

    if (token) void gmailInviteService.claim(token).catch(() => undefined);
  }, [signedIn]);
};

const projectFromPath = (): string | undefined => window.location.pathname.match(/^\/planning\/([^/?#]+)/)?.[1];

/**
 * Back from Google's consent page (``?gmail_invites=<batch>`` or ``?gmail_invites_error=``): reopen Invite people,
 * where the contacts are ready to choose from, or Refer & earn, where Invite all shows its progress.
 */
export const GmailInviteReturn: React.FC = () => {
  const { user } = useAuth();
  const [{ open, notice, refer }, setReturn] = useState(readReturn);
  const close = () => setReturn({ open: false, notice: null, refer });

  useStripReturnParams();
  useInviteClaim(Boolean(user));

  return refer
    ? <ReferralModal gmailNotice={notice} initialMode="refer" isOpen={open} onClose={close} projectId={projectFromPath()} />
    : <InvitePeopleModal isOpen={open} notice={notice} onClose={close} projectId={projectFromPath()} />;
};
